/**
 * 認証コンテキスト
 *
 * Firebase Authenticationを使用したユーザー認証を管理
 */

import { createContext, useContext, useState, useEffect } from 'react';
import {
  getAuth,
  signInWithPopup,
  GoogleAuthProvider,
  signOut,
  onAuthStateChanged
} from 'firebase/auth';
import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { app, db } from '../services/firebase';
import { checkAndResetMonthlyCount } from '../services/users';

// 認証コンテキストを作成
const AuthContext = createContext(null);

/**
 * 認証プロバイダーコンポーネント
 */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const auth = getAuth(app);
  const googleProvider = new GoogleAuthProvider();

  /**
   * LocalStorageに認証情報を保存（拡張機能用）
   */
  async function saveAuthToLocalStorage(firebaseUser) {
    try {
      // 常に新しいトークンを取得（期限切れの場合は自動リフレッシュ）
      const idToken = await firebaseUser.getIdToken(true);

      const authData = {
        uid: firebaseUser.uid,
        email: firebaseUser.email,
        displayName: firebaseUser.displayName,
        photoURL: firebaseUser.photoURL,
        idToken: idToken
      };

      localStorage.setItem('knovelo_auth_user', JSON.stringify(authData));
      console.log('[KnoVelo Auth] LocalStorageに認証情報を保存しました（トークン更新）');
      return authData;
    } catch (e) {
      console.error('[KnoVelo Auth] LocalStorage保存エラー:', e);
      return null;
    }
  }

  // 認証状態の変更を監視
  useEffect(() => {
    console.log('[KnoVelo Auth] 認証状態の監視を開始');

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        console.log('[KnoVelo Auth] ユーザーがログイン:', firebaseUser.email);

        // Firestoreにユーザー情報を保存/更新
        await syncUserToFirestore(firebaseUser);

        // LocalStorageに保存（拡張機能が読み取れるように）- 常に新しいトークンを取得
        const authData = await saveAuthToLocalStorage(firebaseUser);

        if (authData) {
          setUser({
            uid: authData.uid,
            email: authData.email,
            displayName: authData.displayName,
            photoURL: authData.photoURL
          });

          // ページ読み込み時に即座にトークン更新を試みる（既存ログインの場合）
          console.log('[KnoVelo Auth] トークンを即座に更新しました');
        }
      } else {
        console.log('[KnoVelo Auth] ユーザーがログアウト');
        setUser(null);

        // LocalStorageからも削除
        try {
          localStorage.removeItem('knovelo_auth_user');
          console.log('[KnoVelo Auth] LocalStorageから認証情報を削除しました');
        } catch (e) {
          console.error('[KnoVelo Auth] LocalStorage削除エラー:', e);
        }
      }
      setLoading(false);
    });

    // クリーンアップ
    return () => unsubscribe();
  }, [auth]);

  // トークンの定期リフレッシュ（10分ごと）
  useEffect(() => {
    if (!user) return;

    const refreshToken = async () => {
      const currentUser = auth.currentUser;
      if (currentUser) {
        console.log('[KnoVelo Auth] トークンを定期リフレッシュ中...');
        await saveAuthToLocalStorage(currentUser);
      }
    };

    // 10分ごとにリフレッシュ（IDトークンは1時間で期限切れ）
    const intervalId = setInterval(refreshToken, 10 * 60 * 1000);

    // ページがフォーカスされた時もリフレッシュ
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshToken();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // 拡張機能からのトークンリフレッシュ要求を処理
    const handleExtensionTokenRefresh = () => {
      console.log('[KnoVelo Auth] 拡張機能からトークンリフレッシュ要求を受信');
      refreshToken();
    };
    window.addEventListener('knovelo-token-refresh', handleExtensionTokenRefresh);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('knovelo-token-refresh', handleExtensionTokenRefresh);
    };
  }, [user, auth]);

  /**
   * Firestoreにユーザー情報を同期
   * @param {Object} firebaseUser - Firebaseユーザーオブジェクト
   */
  async function syncUserToFirestore(firebaseUser) {
    try {
      const userRef = doc(db, 'users', firebaseUser.uid);
      const userDoc = await getDoc(userRef);

      if (userDoc.exists()) {
        // 既存ユーザー: lastLoginを更新
        await setDoc(userRef, {
          lastLogin: serverTimestamp()
        }, { merge: true });

        // 既存ユーザー: 月次リセットをチェック
        await checkAndResetMonthlyCount(firebaseUser.uid);
      } else {
        // 新規ユーザー: 初期データを作成
        await setDoc(userRef, {
          userId: firebaseUser.uid,
          email: firebaseUser.email,
          displayName: firebaseUser.displayName,
          photoURL: firebaseUser.photoURL,
          plan: 'free',
          stripeCustomerId: '',
          stripeSubscriptionId: '',
          createdAt: serverTimestamp(),
          lastLogin: serverTimestamp(),
          storageUsed: 0,
          monthlyQueryCount: 0,
          monthlyQueryLimit: 5, // Freeプランのデフォルト
          lastResetDate: serverTimestamp(), // 月次リセット用の日付
          rewardedAdCount: 0,
          lastRewardedAdDate: '',
          searchCredits: 0,
          settings: {
            language: 'ja',
            notifications: true,
            theme: 'light'
          }
        });
        console.log('[KnoVelo Auth] 新規ユーザーを作成しました');
      }
    } catch (err) {
      console.error('[KnoVelo Auth] Firestoreへのユーザー同期エラー:', err);
    }
  }

  /**
   * Googleでログイン
   */
  async function loginWithGoogle() {
    setError(null);
    try {
      console.log('[KnoVelo Auth] Googleログインを開始');
      const result = await signInWithPopup(auth, googleProvider);
      console.log('[KnoVelo Auth] Googleログイン成功:', result.user.email);
      return result.user;
    } catch (err) {
      console.error('[KnoVelo Auth] Googleログインエラー:', err);
      setError(getErrorMessage(err.code));
      throw err;
    }
  }

  /**
   * ログアウト
   */
  async function logout() {
    try {
      console.log('[KnoVelo Auth] ログアウトを開始');
      await signOut(auth);
      console.log('[KnoVelo Auth] ログアウト成功');
    } catch (err) {
      console.error('[KnoVelo Auth] ログアウトエラー:', err);
      setError(getErrorMessage(err.code));
      throw err;
    }
  }

  /**
   * エラーコードを日本語メッセージに変換
   * @param {string} code - Firebaseエラーコード
   * @returns {string} エラーメッセージ
   */
  function getErrorMessage(code) {
    const messages = {
      'auth/popup-closed-by-user': 'ログインがキャンセルされました',
      'auth/network-request-failed': 'ネットワークエラーが発生しました',
      'auth/too-many-requests': 'リクエストが多すぎます。しばらく待ってからお試しください',
      'auth/user-disabled': 'このアカウントは無効化されています',
      'auth/operation-not-allowed': 'この認証方法は許可されていません'
    };
    return messages[code] || 'ログイン中にエラーが発生しました';
  }

  const value = {
    user,
    loading,
    error,
    loginWithGoogle,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

/**
 * 認証コンテキストを使用するカスタムフック
 */
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthはAuthProvider内で使用する必要があります');
  }
  return context;
}

export default AuthContext;
